import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';
import defaultImg from '../../../asset/project/default_1.png';
import { WorkData, convertWorkSizeToString, getMainSizeData } from '../../../shared/dto/EntityRepository';
import BlockWithVisible from '../BlockWithVisible';
import { usesWorkDuration } from '../../../shared/enum/EnumRepository';
import { shrinkString } from '../../../shared/util/shrinkString';
import { useOptimizedImageUrl } from '../../../shared/hooks/useOptimizedImageUrl';
import { useWorkPropertyVisible } from '../../../shared/hooks/useApi/useWorkPropertyVisible';
import { useWorkContentModeStore } from '../../../shared/store/workContentModeStore';
import { resolveWorkMaterial, resolveWorkSizeText, resolveWorkTitle } from '../../../shared/util/workDisplayText';

interface WorkInfoProps {
  data: WorkData;
  handleClick: () => void
}

const WorkInfoMobile: React.FC<WorkInfoProps> = ({ data, handleClick }) => {
  const { workPropertyVisible } = useWorkPropertyVisible();
  const workContentMode = useWorkContentModeStore((s) => s.workContentMode);
  const imgContainerRef = useRef<HTMLDivElement>(null);
  const imageUrl = useOptimizedImageUrl(data.uploadFile?.originUrl ?? '', imgContainerRef);

  const engVisible = workPropertyVisible?.engVisible !== false;
  const displayTitle = useMemo(
    () => resolveWorkTitle(data, workContentMode, engVisible),
    [data, workContentMode, engVisible],
  );
  const displayMaterial = useMemo(
    () => resolveWorkMaterial(data, workContentMode, engVisible),
    [data, workContentMode, engVisible],
  );
  const shrinkedTitle = useMemo(() => shrinkString(displayTitle, 20), [displayTitle]);
  const sizeString = useMemo(() => {
    if (usesWorkDuration(data.workType)) {
      return data.duration || '-';
    }
    return convertWorkSizeToString(
      getMainSizeData(data.sizes, data.mainSizeUnit),
      data.dimensionsType,
      resolveWorkSizeText(data),
      'Variable',
    );
  }, [data]);

  return (
    <WorkInfoComp onClick={handleClick} $isPublic={data.isPublic !== false}>
      <ImageBlock>
        <ImgWrapper ref={imgContainerRef}>
          <ThumbnailImg src={imageUrl === '' ? defaultImg : imageUrl} alt={displayTitle} />
        </ImgWrapper>
      </ImageBlock>
      <TitleBlock>
        <TitleText>{shrinkedTitle}</TitleText>
      </TitleBlock>
      <BlockWithVisible
        width={"1.5"}
        value={sizeString} />
      <BlockWithVisible
        width={"1.5"}
        value={displayMaterial === '' ? '-' : displayMaterial} />
      <BlockWithVisible
        width={"0.8"}
        value={data.prodYear === '' || data.prodYear === null ? '-' : data.prodYear} />
    </WorkInfoComp>
  )
}

const WorkInfoComp = styled.article<{ $isPublic: boolean }>`
  position: relative;
  display: flex;
  width: 100%;
  height: 38px;
  min-height: 38px;
  cursor: pointer;
  
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_04};
  opacity: ${({ $isPublic }) => (!$isPublic) ? 0.6 : 1};
`;

const ImageBlock = styled.div`
  height: 100%;
  flex: 0.6;
  min-width: 40px;
  max-width: 50px;
  display: flex;
  align-items: center;
  justify-content: flex-start;

  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;


const ImgWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 84%;

  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-right: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
`;

const ThumbnailImg = styled.img`
  width: 80%;
  height: 96%;
  object-fit: contain;
  // object-position: left center;
`;

const TitleBlock = styled.div`
  flex: 2.2;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  padding-left: 6px;
`;

const TitleText = styled.div`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export default WorkInfoMobile;
